import { useEffect, useState, type RefObject } from "react";

/**
 * Measures viewport space around an anchor and decides whether the calendar
 * should open up/down, align left/right, and use the stacked or side-by-side
 * layout. Coordinates are viewport-fixed (the consumer renders the popover in
 * a body-level portal with ``position: fixed``) so the calendar escapes any
 * sticky stacking context on the page. Re-runs on resize + scroll while open.
 */
export interface CalendarPosition {
  /** Viewport-fixed top in px, or null when ``bottom`` is the anchored edge. */
  top: number | null;
  /** Viewport-fixed bottom in px, used for above-anchor placement. */
  bottom: number | null;
  /** Viewport-fixed left in px, or null when ``right`` is the anchored edge. */
  left: number | null;
  /** Viewport-fixed right in px, used for right-aligned placement. */
  right: number | null;
  stacked: boolean;
}

const SIDE_W = 660;
const STACK_W = 320;
const SIDE_H = 420;
const STACK_H = 760;
const GAP = 8;

export function useCalendarPosition(
  anchor: RefObject<HTMLElement | null>,
  open: boolean,
  popover?: RefObject<HTMLElement | null>,
): CalendarPosition {
  const [pos, setPos] = useState<CalendarPosition>({
    top: 0,
    bottom: null,
    left: 0,
    right: null,
    stacked: false,
  });

  useEffect(() => {
    if (!open || !anchor.current) return;

    const calc = () => {
      const node = anchor.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const fitsLeftAligned = rect.left + SIDE_W <= vw - GAP;
      const fitsRightAligned = rect.right - SIDE_W >= GAP;
      const stacked = !fitsLeftAligned && !fitsRightAligned;
      const calW = stacked ? STACK_W : SIDE_W;
      // Prefer the popover's real rendered height — the estimates below flip
      // placement before the first measure, but once mounted we use the true
      // height so a taller-than-estimated calendar can't get its header strip
      // clipped off the top of the viewport when it opens upward.
      const measuredH = popover?.current?.offsetHeight ?? 0;
      const calH = measuredH || (stacked ? STACK_H : SIDE_H);

      const spaceBelow = vh - rect.bottom - GAP;
      const spaceAbove = rect.top - GAP;
      // Flip up only when below can't fit AND above genuinely has more room.
      const openAbove = spaceBelow < calH && spaceAbove > spaceBelow;

      // Vertical: always anchor by an explicit top, clamped into the viewport.
      // Opening up used to anchor the *bottom* edge with no top clamp, so a
      // low trigger pushed the nav bar + month/weekday headers above the
      // viewport (they vanished). Clamping top to >= GAP keeps them visible;
      // if the calendar is taller than the whole viewport the bottom gives
      // instead (headers/nav are the priority).
      let topVal: number;
      if (openAbove) {
        topVal = Math.max(GAP, rect.top - GAP - calH);
      } else {
        topVal = Math.min(rect.bottom + GAP, Math.max(GAP, vh - GAP - calH));
      }

      // Horizontal: prefer left-aligned to the anchor; flip to right-aligned
      // when left-align would overflow. When stacked (narrow viewport),
      // clamp the left edge so the popover never bleeds off the viewport.
      let leftVal: number | null = Math.round(rect.left);
      let rightVal: number | null = null;
      if (!fitsLeftAligned && !stacked) {
        leftVal = null;
        rightVal = Math.round(vw - rect.right);
      } else if (stacked) {
        leftVal = Math.min(Math.round(rect.left), vw - calW - GAP);
        leftVal = Math.max(GAP, leftVal);
      }

      setPos({
        top: Math.round(topVal),
        bottom: null,
        left: leftVal,
        right: rightVal,
        stacked,
      });
    };

    calc();
    // Re-measure once the popover has painted (first calc may run before the
    // portal node is laid out) and whenever its height changes — e.g. a month
    // with six week-rows vs five, which would otherwise re-clip.
    const raf = requestAnimationFrame(calc);
    window.addEventListener("resize", calc);
    // Reposition on scroll too — when the page scrolls under the (sticky)
    // anchor the popover needs to track it. ``true`` for capture so we catch
    // scrolls on inner overflow containers (the AppShell's main scroller),
    // not just window scrolls.
    window.addEventListener("scroll", calc, true);
    let ro: ResizeObserver | undefined;
    if (popover?.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(calc);
      ro.observe(popover.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", calc);
      window.removeEventListener("scroll", calc, true);
      ro?.disconnect();
    };
  }, [open, anchor, popover]);

  return pos;
}
